import { Flex, ListItem, OrderedList, Text, useToast } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router";

import { TenantRead } from "@nens/rana/api-v1";
import { RanaLoading, RanaModal } from "@nens/ranalib";

import { cancelButtonText } from "@/translations";

import { useDeleteTenants } from "../api/delete-tenant";

interface AdminDeleteTenantModalProps {
  tenants: TenantRead[];
  onClose: () => void;
}

export function AdminDeleteTenantModal({ tenants, onClose }: AdminDeleteTenantModalProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const toast = useToast();
  const { tenant } = useParams();

  const { mutateAsync, isMutating } = useDeleteTenants();

  const deleteTenants = async () => {
    await mutateAsync(tenants.map(({ id }) => id));
    if (tenants.some((t) => t.id === tenant)) {
      navigate("/");
    }
    toast({
      title: intl.formatMessage({
        id: "toast.success",
        defaultMessage: "Success!",
      }),
      description: intl.formatMessage({
        id: "toast.tenants_deleted",
        defaultMessage: "Tenant(s) deleted successfully.",
      }),
    });

    onClose();
  };

  const title = intl.formatMessage({
    id: "header.delete_organisations",
    defaultMessage: "Delete organisations",
  });

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={title}
      maxW="xl"
      h="fit"
      buttons={[
        {
          children: cancelButtonText,
          variant: "tertiary",
          isDisabled: isMutating,
          onClick: onClose,
        },
        {
          children: title,
          variant: "danger",
          isDisabled: isMutating,
          onClick: deleteTenants,
        },
      ]}
    >
      <Flex flexDir="column" gap={2}>
        <Text>
          <FormattedMessage
            id="message.do_you_want_to_delete_organisations"
            defaultMessage="Do you really want to delete these organisations?"
          />
        </Text>
        <OrderedList>
          {tenants.map(({ id, name }) => (
            <ListItem key={id}>
              <Flex alignItems="center">{name}</Flex>
            </ListItem>
          ))}
        </OrderedList>
        {isMutating && <RanaLoading size="md" isCentered />}
      </Flex>
    </RanaModal>
  );
}
